import * as React from 'react';
import {flushSync} from 'react-dom';
import {noop} from 'lodash';
import {
  useFloating,
  flip,
  size,
  autoUpdate,
  SideObject,
  useInteractions,
  inner,
  useInnerOffset,
  useClick,
  useListNavigation,
  useDismiss,
  useRole,
  useTypeahead,
  FloatingFocusManager,
  FloatingOverlay,
  shift,
  offset,
  useFloatingNodeId,
  FloatingNode,
  useMergeRefs,
} from '@floating-ui/react';

import {emptyArray, emptyObject} from '@packages/utilities/fp/utils';
import {useSize, type SizeContextSizeValue} from '@packages/utilities/styled';
import {useDevAlert} from '@packages/systems/spring-hooks';

import {CheckDefaultIcon} from '../../icons';
import {fixedForwardRef, isInJest, renderElement} from '../../utils';

import {isOptionGroup} from './_types';
import type {SelectProps, SelectOption, SelectOptions} from './_types';
import {SPACING_BASE_VALUE} from '../../hooks/useFloating/useFloatingBase';
import {Divider} from '../Divider';
import {OverlayPortal} from '../OverlayPortal';
import {Spacer} from '../Spacer';
import {Spinner} from '../Spinner';
import {HStack, StackItem, VStack} from '../Stack';
import {Text, type TextProps} from '../Text';
import {Tooltip, TooltipGroup} from '../Tooltip';
import {View} from '../View';

import {
  OptionView,
  OptionGroupView,
  OptionHStack,
  SelectMenu,
  SelectMenuOptionsContainer,
  SelectWrapperView,
  EmptyIconSpacer,
  CheckmarkView,
  OptionIconPrefixView,
  NoteView,
  SELECT_MENU_MIN_WIDTH,
  NoteContainerView,
} from './Select.styles';

import {SelectTrigger} from './SelectTrigger';
import {useFormGroupId} from '../FormGroup';
import Mask from '../Mask';

const SELECT_NOTE_ID = 'select-option-note-contents';

/**
 * Removes any empty groups from our options array.
 * This is necessary so that we can properly exclude them from our selects.
 * A group with no options is not a valid state.
 * This function allows our users to make programmatically constructed arrays without checking option lengths.
 *
 * While we could enforce this on a type level (i.e. options: NonEmptyTuple<SelectOptions>), doing so would error-out a lot of existing code.
 */
const filterEmptyGroups = <TOptions extends SelectOptions>(
  options: TOptions
) => {
  return options.filter((option) => {
    if (isOptionGroup(option)) {
      return option.options.length > 0;
    }
    return true;
  });
};

const initializeOptions = <TOptions extends SelectOptions>(
  options: TOptions
) => {
  const optionsWithoutEmptyGroups = filterEmptyGroups(options);

  const flattenedOptions: Array<TOptions[number]> = [];
  optionsWithoutEmptyGroups.forEach((option) => {
    if (isOptionGroup(option)) {
      flattenedOptions.push(...option.options);
    } else {
      flattenedOptions.push(option);
    }
  });

  const optionsMap = flattenedOptions.reduce(
    (acc, option, index) => {
      if (!isOptionGroup(option)) {
        acc[option.value] = {index};
      }
      return acc;
    },
    {} as Record<string, {index: number}> // right now just returning index, but other information might be useful.
  );

  return {
    optionsMap,
    flattenedOptions,
    optionsWithoutEmptyGroups,
  };
};

const getIndexFromValue = ({
  optionsMap,
  value,
}: {
  optionsMap: Record<string, {index: number}>;
  value?: string | null;
}) => {
  /**
   * We need to explicitly check for null or undefined here, instead of a simple truthiness check of `if (!value)`
   *
   * The reason for this is that our `Select` options can have either a `Deselect` option `customClickActions` associated with them.
   * These entries generally have `value=''` to indicate that they are not options, but are still selectable choices.
   *
   * @TODO - Currently, `optionsMap` assume a unique value for each option. If there are multiple empty strings, this will not work... I think?
   */
  if (value === null || value === undefined) {
    return -1;
  }

  return optionsMap[value]?.index ?? -1;
};

/**
 * We use `value=''` as a placeholder value meaning "None selected" and also "This option has a clickable option".
 *
 * In both of these cases, we do not want our selected index to be the same as the index of the option with `value=''`.
 */
const getSelectedIndexFromValue = ({
  optionsMap,
  value,
}: {
  optionsMap: Record<string, {index: number}>;
  value?: string | null;
}) => {
  if (value === '') {
    return -1;
  }
  return getIndexFromValue({optionsMap, value});
};

// In the event of the Select dropdown opening in a small area, we want to show at least a few options.
// Without this logic, the dropdown would take up only the available height, which could end up being really small,
// where only half an option is shown. We're choosing an arbirtrary number here as the smallest acceptable height.
// 96 is the equivalent of 4 options in the medium size context.
// It's not worth it though to try to measure the height of each option in the event of description text or different size contexts.
// So we're not saying this is always exactly the height of 4 options, it's just what we used as a baseline.
const DEFAULT_SCROLLABLE_OPTIONS_MIN_HEIGHT = 96;

// Used for adding space between the Select dropdown and the edges of containers like the browser window
const BOUNDARY_SPACING = 8;

// Gap between the trigger button and the dropdown when no `gutter` is set.
const DEFAULT_GUTTER = 4;

// Horizontal offset that accounts for the position difference between the trigger button label text and the dropdown option label text.
// Used with the `shouldPositionInline` prop for when we want to place these exactly in the same location, both vertically and horizontally.
// These pixel amounts are the sum of the checkmark icon width and left/right padding.
const MEDIUM_SIZE_DROPDOWN_OFFSET = 24;
const X_LARGE_SIZE_DROPDOWN_OFFSET = 28;

// When the `_shouldUseLegacyAlignment` prop is used, it shifts the trigger button label text a little more to the right to match our legacy Selects.
// When using both `_shouldLegacyAlignment` and `shouldPositionInline`, we have to account for this offset change.
const MEDIUM_SIZE_LEGACY_ALIGNMENT_OFFSET = -4;
const X_LARGE_SIZE_LEGACY_ALIGNMENT_OFFSET = -1;

// Used when calculating the dropdown height with note text. Accounts for the top/bottom padding in the dropdown and in the note and the divider.
const MEDIUM_SIZE_DROPDOWN_ADDITIONAL_SPACING = 17;
const X_LARGE_SIZE_DROPDOWN_ADDITIONAL_SPACING = 20;

// Line height used by our typography system
const MEDIUM_SIZE_LINE_HEIGHT = 16;
const X_LARGE_SIZE_LINE_HEIGHT = 18;

// These are somewhat arbitrary numbers used when setting the note height based on the number of characters in the note text.
// These are just estimates of what we think will work best to correctly get the right number of lines in the note without
// having to actually render each note and measure the real height, as that may be slower to do, performance-wise.
// So if you ever find yourself with note height issues, just know that these numbers are arbitrary and can be adjusted as needed.
const MEDIUM_SIZE_AVERAGE_CHARACTER_WIDTH = 6;
const X_LARGE_SIZE_AVERAGE_CHARACTER_WIDTH = 6.4;

const NOTE_TOP_BOTTOM_PADDING = 4;

const calculateNoteHeight = (
  noteText: string = '',
  popupWidth = SELECT_MENU_MIN_WIDTH,
  themeSize: SizeContextSizeValue
) => {
  const isXLargeSize = themeSize === 'x-large';
  const noteLineHeight = isXLargeSize
    ? X_LARGE_SIZE_LINE_HEIGHT
    : MEDIUM_SIZE_LINE_HEIGHT;
  const averageCharacterWithInPixels = isXLargeSize
    ? X_LARGE_SIZE_AVERAGE_CHARACTER_WIDTH
    : MEDIUM_SIZE_AVERAGE_CHARACTER_WIDTH;

  const characters = noteText.length;
  const numberOfLines = Math.ceil(
    (characters * averageCharacterWithInPixels) / popupWidth
  );
  const noteHeight = numberOfLines * noteLineHeight + NOTE_TOP_BOTTOM_PADDING;

  return noteHeight;
};

const getMaxNoteHeight = (
  flattenedOptions: SelectOptions,
  defaultNoteText: string = '',
  popupWidth = SELECT_MENU_MIN_WIDTH,
  themeSize: SizeContextSizeValue
) => {
  let maxHeight = 0;

  flattenedOptions.forEach((option) => {
    if (option.note) {
      const noteHeight = calculateNoteHeight(
        option.note,
        popupWidth,
        themeSize
      );
      maxHeight = Math.max(maxHeight, noteHeight);
    }
  });

  if (defaultNoteText) {
    const noteHeight = calculateNoteHeight(
      defaultNoteText,
      popupWidth,
      themeSize
    );
    maxHeight = Math.max(maxHeight, noteHeight);
  }

  return maxHeight;
};

// Additional height in the dropdown that's due to the padding on the top and bottom of the dropdown
// as well as from the note text and divider
const getDropdownAdditionalSpacing = (themeSize: SizeContextSizeValue) =>
  themeSize === 'x-large'
    ? X_LARGE_SIZE_DROPDOWN_ADDITIONAL_SPACING
    : MEDIUM_SIZE_DROPDOWN_ADDITIONAL_SPACING;

const isDeprecatedDisabledPropertyUsed = <TOptions extends SelectOptions>(
  flattenedOptions: TOptions
) => flattenedOptions.some((option) => option.disabled);

/**
 * # Select
 *
 * The Select component allows users to pick a value from predefined options by rendering a custom `select` element.
 *
 * ## Usage
 *
 * ```jsx
 * import { Select } from "@packages/systems/spring";
 * import { options } from "../data/options";
 *
 * function Example() {
 *   const [value, setValue] = React.useState(options[0].value);
 *   const handleOnChange = (next) => setValue(next.value);
 *
 *   return <Select options={options} onChange={handleOnChange} value={value} />;
 * }
 * ```
 */
const SelectBase = <TOptions extends SelectOptions>(
  {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    colorVariant = 'default',
    defaultIsOpen = false,
    defaultNoteText,
    dropdownWidth,
    gutter,
    id: idProp,
    isBranded = false,
    isDisabled,
    isError,
    isLoading,
    isOpen: controlledIsOpen,
    onChange,
    onRequestClose,
    onRequestOpen,
    onPreselect,
    options = emptyArray as [],
    optionTruncationProps,
    placeholder = 'Select...',
    renderTriggerButtonLabel,
    shouldNavigateDisabledOptions = false,
    shouldPositionInline = false,
    shouldTruncateButtonText = false,
    shouldHideChevron,
    shouldDropdownMatchTriggerWidth = true,
    _shouldUseLegacyAlignment = false,
    style,
    value,
    variant,
    verticalOptionAlignment = 'top',
    ...props
  }: SelectProps<TOptions>,
  ref: React.Ref<HTMLDivElement>
) => {
  const id = useFormGroupId(idProp);
  const listRef = React.useRef<Array<HTMLElement | null>>([]);
  const listContentRef = React.useRef<Array<string | null>>([]);
  const overflowRef = React.useRef<SideObject>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const optionsContainerRef = React.useRef<HTMLDivElement>(null);
  const noteContainerRef = React.useRef<HTMLDivElement>(null);
  const nodeId = useFloatingNodeId();

  const {flattenedOptions, optionsMap} = React.useMemo(
    () => initializeOptions(options),
    [options]
  );

  const [uncontrolledIsOpen, setUncontrolledIsOpen] =
    React.useState(defaultIsOpen);
  const isOpen = controlledIsOpen ?? uncontrolledIsOpen;

  const [selectedIndex, setSelectedIndex] = React.useState(
    getSelectedIndexFromValue({optionsMap, value})
  );
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const [innerOffset, setInnerOffset] = React.useState(0);
  const [touch, setTouch] = React.useState(false);
  const [dropdownMaxHeight, setDropdownMaxHeight] = React.useState<number>();
  const [scrollableOptionsMinHeight, setScrollableOptionsMinHeight] =
    React.useState<number>();
  const [scrollableOptionsMaxHeight, setScrollableOptionsMaxHeight] =
    React.useState<number>();

  useDevAlert(
    isDeprecatedDisabledPropertyUsed(flattenedOptions),
    "Some option objects in the Select's `options` prop are using the `disabled` property. Please use `isDisabled` instead."
  );

  // This is necessary to keep the `selectedIndex` in sync with the `value`
  // in the event that the value is programmatically changed by something
  // without interacting with the Select dropdown itself.
  React.useEffect(() => {
    setSelectedIndex(getSelectedIndexFromValue({optionsMap, value}));
  }, [optionsMap, value]);

  const themeSize = useSize();

  const setValueBasedOnSelectedIndex = (index: number) => {
    const option = flattenedOptions[index];

    if (option && !option.customClickAction && !isOptionGroup(option)) {
      setSelectedIndex(index);
      /**
       * The `flattenedOptions` function is not able to narrow `TOptions` successfully (see above)
       * This is fine for now, since we know what our type shapes are within our own implementation.
       */
      // @ts-expect-error - TS2345 - Argument of type 'string | number | null | undefined' is not assignable to parameter of type 'string | null | undefined'.
      onPreselect?.(option.value, {option, changeType: 'changeEnd'});
      /**
       * The flattenedOptions function is not able to narrow TOptions successfully (see above)
       * This is fine for now, since we know what our type shapes are within our own implementation.
       */
      // @ts-expect-error - TS2345 - Argument of type 'string | number | null | undefined' is not assignable to parameter of type 'string | null | undefined'.
      onChange?.(option.value, {option, changeType: 'changeEnd'});
    }
  };

  const onActiveIndexChange = (index: number | null) => {
    if (index !== activeIndex) {
      setActiveIndex(index);
      if (index !== null) {
        const preselectOption = flattenedOptions[index];
        if (
          preselectOption &&
          !(preselectOption.isDisabled || preselectOption.disabled)
        ) {
          /**
           * The `flattenedOptions` function is not able to narrow `TOptions` successfully (see above)
           * This is fine for now, since we know what our type shapes are within our own implementation.
           */
          // @ts-expect-error - TS2345 - Argument of type 'string | number | null | undefined' is not assignable to parameter of type 'string | null | undefined'.
          onPreselect?.(preselectOption.value, {
            option: preselectOption,
            changeType: 'change',
          });
        }
      }
    }
  };

  if (!isOpen) {
    if (innerOffset !== 0) setInnerOffset(0);
  }

  const setIsOpen = (nextIsOpen: boolean) => {
    if (controlledIsOpen === undefined) {
      setUncontrolledIsOpen(nextIsOpen);
    }
  };

  const onOpenChange = (
    nextIsOpen: boolean,
    event?: Event | React.SyntheticEvent
  ) => {
    const preselectOption = flattenedOptions[selectedIndex];

    if (nextIsOpen) {
      /**
       * The `flattenedOptions` function is not able to narrow `TOptions` successfully (see above)
       * This is fine for now, since we know what our type shapes are within our own implementation.
       */
      // @ts-expect-error - TS2345 - Argument of type 'string | number | null | undefined' is not assignable to parameter of type 'string | null | undefined'.
      onPreselect?.(preselectOption.value, {
        option: preselectOption,
        changeType: 'changeStart',
      });
      onRequestOpen?.(event);
    } else {
      if (
        preselectOption &&
        !(preselectOption.isDisabled || preselectOption.disabled)
      ) {
        /**
         * The `flattenedOptions` function is not able to narrow `TOptions` successfully (see above)
         * This is fine for now, since we know what our type shapes are within our own implementation.
         */
        // @ts-expect-error - TS2345 - Argument of type 'string | number | null | undefined' is not assignable to parameter of type 'string | null | undefined'.
        onPreselect?.(preselectOption.value, {
          option: preselectOption,
          changeType: 'changeCancel',
        });
      }
      onRequestClose?.(event);
    }
    setIsOpen(nextIsOpen);
  };

  let dropdownCrossAxisOffset =
    themeSize === 'x-large'
      ? X_LARGE_SIZE_DROPDOWN_OFFSET
      : MEDIUM_SIZE_DROPDOWN_OFFSET;
  if (_shouldUseLegacyAlignment) {
    dropdownCrossAxisOffset +=
      themeSize === 'x-large'
        ? X_LARGE_SIZE_LEGACY_ALIGNMENT_OFFSET
        : MEDIUM_SIZE_LEGACY_ALIGNMENT_OFFSET;
  }

  const shouldApplyInlinePositioningOffset =
    shouldPositionInline && selectedIndex >= 0;

  const middleware = React.useMemo(
    () =>
      isInJest
        ? []
        : [
            // Positions the Select dropdown on top of the trigger button when `shouldPositionInline` is true.
            // Positions the Select dropdown above or below the trigger button when `shouldPositionInline` is false.
            shouldPositionInline
              ? inner({
                  listRef,
                  overflowRef,
                  scrollRef,
                  index: selectedIndex,
                  offset: innerOffset,
                  padding: BOUNDARY_SPACING,
                  minItemsVisible: touch ? 8 : 4,
                  referenceOverflowThreshold: 20,
                })
              : undefined,
            // Adjusts the offset of the Select dropdown:
            // When `shouldPositionInline` is true and an option is currently selected, we shift the dropdown to the
            // left a little to place the dropdown option horizontally in the same position as the trigger button label text.
            // When `shouldPositionInline` is false or an option is not current selected, we add a small gap between the
            // trigger button and the Select dropdown when the Select dropdown opens above or below the trigger button.
            shouldApplyInlinePositioningOffset
              ? offset({crossAxis: -dropdownCrossAxisOffset})
              : offset(
                  gutter != null ? gutter * SPACING_BASE_VALUE : DEFAULT_GUTTER
                ),
            // When the user resizes the window and the Select dropdown position needs to adjust:
            // If `shouldPositionInline` is true, the Select dropdown shifts up or down as needed
            // If `shouldPositionInline` is false, the Select dropdown flips above or below the trigger button as needed
            shouldPositionInline
              ? shift({crossAxis: true, padding: BOUNDARY_SPACING})
              : flip({padding: BOUNDARY_SPACING}),
            // Size of the Select dropdown
            size({
              apply({availableHeight, rects}) {
                // Allows the Select dropdown to take up as much or as little height as it needs, or expand/contract based on available space in the window.
                // Also sets the height of the scrollable options container to account for the note text, if there is any.
                flushSync(() => {
                  const noteContainerHeight =
                    noteContainerRef.current?.getBoundingClientRect().height;

                  let scrollableContentMaxHeight;
                  if (
                    typeof availableHeight !== 'undefined' &&
                    typeof noteContainerHeight !== 'undefined'
                  ) {
                    scrollableContentMaxHeight =
                      availableHeight -
                      noteContainerHeight -
                      getDropdownAdditionalSpacing(themeSize);
                  }

                  const scrollableContentMinHeight = optionsContainerRef.current
                    ?.scrollHeight
                    ? Math.min(
                        optionsContainerRef.current.scrollHeight,
                        DEFAULT_SCROLLABLE_OPTIONS_MIN_HEIGHT
                      )
                    : undefined;

                  setDropdownMaxHeight(availableHeight);
                  setScrollableOptionsMinHeight(scrollableContentMinHeight);
                  setScrollableOptionsMaxHeight(scrollableContentMaxHeight);
                });

                /**
                 * Makes the Select dropdown width slightly larger than the trigger button when the `shouldPositionInline` prop is used
                 * since we want to position the current option in the dropdown horizontally and vertically over the trigger button label text
                 */
                const automaticDropdownWidthWithoutOffset =
                  rects.reference.width;
                const automaticDropdownWidthWithOffset =
                  automaticDropdownWidthWithoutOffset + dropdownCrossAxisOffset;
                const automaticDropdownWidth =
                  shouldApplyInlinePositioningOffset
                    ? automaticDropdownWidthWithOffset
                    : automaticDropdownWidthWithoutOffset;

                /**
                 * Determine how the dropdown gets its width.
                 *
                 * If shouldDropdownMatchTriggerWidth is false and no dropdownWidth is set,
                 * we skip applying an inline width and let CSS (via fit-content)
                 * set the width of the dropdown based on its content.
                 */
                if (
                  dropdownWidth !== undefined ||
                  shouldDropdownMatchTriggerWidth
                ) {
                  const finalWidth = dropdownWidth ?? automaticDropdownWidth;

                  Object.assign(scrollRef.current?.style ?? {}, {
                    width: `${finalWidth}px`,
                  });
                }
              },
              padding: BOUNDARY_SPACING,
            }),
          ],
    [
      dropdownWidth,
      shouldDropdownMatchTriggerWidth,
      shouldPositionInline,
      shouldApplyInlinePositioningOffset,
      dropdownCrossAxisOffset,
      innerOffset,
      selectedIndex,
      touch,
      themeSize,
      noteContainerRef,
      optionsContainerRef,
      flattenedOptions,
      defaultNoteText,
      isInJest,
    ]
  );

  const {refs, floatingStyles, context} = useFloating({
    placement: 'bottom-start',
    nodeId,
    open: isOpen,
    onOpenChange,
    whileElementsMounted: isInJest ? undefined : autoUpdate,
    transform: false,
    middleware,
  });

  const {getReferenceProps, getFloatingProps, getItemProps} = useInteractions([
    useClick(context, {
      // Prevents a disabled Select trigger button from opening the dropdown on click or Enter/Space keypress
      enabled: !isDisabled,
      event: 'mousedown',
    }),
    useDismiss(context, {
      bubbles: false,
    }),
    useRole(context, {role: 'listbox'}),
    useInnerOffset(context, {
      enabled: true,
      onChange: setInnerOffset,
      overflowRef,
      scrollRef,
    }),
    useListNavigation(context, {
      // Prevents a disabled Select trigger button from opening the dropdown on UpArrow or DownArrow keypress when focused
      enabled: !isDisabled,
      listRef,
      activeIndex,
      selectedIndex,
      onNavigate: onActiveIndexChange,
      /**
       * disabledIndices is deliberately an empty array when shouldNavigateDisabledOptions is true.
       * This is to override useListNavigation's default behavior
       * of skipping indices that have disabled or aria-disabled attributes.
       * This way, disabled options in the dropdown that have tooltips on them
       * can still be reached via the keyboard to show the tooltip.
       */
      disabledIndices: shouldNavigateDisabledOptions ? [] : undefined,
    }),
    useTypeahead(context, {
      // Prevents a disabled Select trigger button from changing the selected value when focused and characters are typed
      enabled: !isDisabled,
      listRef: listContentRef,
      activeIndex,
      onMatch: isOpen ? onActiveIndexChange : setValueBasedOnSelectedIndex,
    }),
  ]);

  const selectedOption = React.useMemo(() => {
    const optionIndex = getSelectedIndexFromValue({optionsMap, value});
    if (optionIndex === -1) {
      return null;
    }

    return flattenedOptions[optionIndex];
  }, [flattenedOptions, optionsMap, value]);

  const noteTextToShow =
    flattenedOptions[activeIndex ?? selectedIndex]?.note || defaultNoteText;

  const popupWidth = scrollRef.current?.getBoundingClientRect?.()?.width;
  const noteMinHeightIfNoteTextExists = React.useMemo(() => {
    return getMaxNoteHeight(
      flattenedOptions,
      defaultNoteText,
      popupWidth,
      themeSize
    );
  }, [flattenedOptions, defaultNoteText, popupWidth, themeSize]);

  const noteMinHeight = noteTextToShow
    ? noteMinHeightIfNoteTextExists
    : undefined;

  const dropdownMinHeight = noteMinHeight
    ? noteMinHeight +
      getDropdownAdditionalSpacing(themeSize) +
      (scrollableOptionsMinHeight || 0)
    : scrollableOptionsMinHeight;

  let triggerButtonLabel;
  let triggerButtonLabelColorVariant: TextProps['colorVariant'];
  let triggerButtonPrefix;
  let triggerButtonSuffix;

  if (selectedOption) {
    triggerButtonLabel = selectedOption.label;

    if (selectedOption.icon) {
      triggerButtonPrefix = renderElement(selectedOption.icon);
    }

    if (selectedOption.suffix) {
      triggerButtonSuffix = selectedOption.suffix;
    }
  } else if (value) {
    /**
     * If no label has been provided, we'll default to the value itself.
     */
    triggerButtonLabel = value;
  } else {
    /**
     * If there is no selectedOption or value, we'll show the placeholder text.
     */
    triggerButtonLabel = placeholder;

    if (colorVariant === 'default') {
      triggerButtonLabelColorVariant = 'secondary';
    }
  }

  if (renderTriggerButtonLabel) {
    /**
     * The flattenedOptions function is not able to narrow TOptions successfully (see above)
     * This is fine for now, since we know what our type shapes are within our own implementation.
     */
    // @ts-expect-error - TS2345 - Argument of type 'string | number | null | undefined' is not assignable to parameter of type 'string | SelectOption | null'.
    triggerButtonLabel = renderTriggerButtonLabel(selectedOption || value);

    /**
     * If a custom render function is provided, we don't want to show the
     * prefix or suffix icons, so we set them to null.
     */
    triggerButtonPrefix = null;
    triggerButtonSuffix = null;
  }

  // Ensure variant is never "solid" and colorVariant is not primary/danger/success
  const safeColorVariant = ['primary', 'danger', 'success'].includes(
    colorVariant as string
  )
    ? 'default'
    : colorVariant;

  const selectMenuRef = useMergeRefs([scrollRef, refs.setFloating]);

  return (
    <SelectWrapperView data-sc="Select" ref={ref} style={style}>
      {/* @ts-expect-error - TS2322 - @SPRING_CLEANING: Types of property 'prefix' are incompatible. */}
      <SelectTrigger
        {...props}
        _shouldUseLegacyAlignment={_shouldUseLegacyAlignment}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        colorVariant={safeColorVariant}
        id={id}
        isBranded={isBranded}
        isDisabled={isDisabled}
        isError={isError}
        isOpen={isOpen}
        prefix={triggerButtonPrefix}
        ref={refs.setReference}
        shouldHideChevron={shouldHideChevron}
        shouldPositionInline={shouldPositionInline}
        shouldTruncateButtonText={shouldTruncateButtonText}
        suffix={triggerButtonSuffix}
        variant={variant}
        {...getReferenceProps({
          onTouchStart() {
            setTouch(true);
          },
          onPointerMove({pointerType}) {
            if (pointerType !== 'touch') {
              setTouch(false);
            }
          },
        })}
        _isPlaceholder={Boolean(
          !value && !selectedOption && placeholder && !renderTriggerButtonLabel
        )}
        _textColorVariant={triggerButtonLabelColorVariant}
      >
        {triggerButtonLabel}
      </SelectTrigger>
      <FloatingNode id={nodeId}>
        {isOpen && (
          <OverlayPortal>
            <FloatingOverlay lockScroll={!touch}>
              <Mask
                shouldAllowPointerEvents={false}
                shouldEscapeViewport
                type="transparent"
              />
              <FloatingFocusManager context={context} modal={false}>
                <SelectMenu
                  ref={selectMenuRef}
                  {...getFloatingProps({
                    onContextMenu(e) {
                      e.preventDefault();
                    },
                    onWheel(e) {
                      // We need to stop our `onWheel` from propagating up to `ScrollView` in the Settings panel.
                      e.stopPropagation();
                    },
                  })}
                  aria-label={ariaLabel}
                  aria-labelledby={ariaLabelledBy}
                  data-automation-id="select-menu"
                  shouldNotMatchTriggerWidth={!shouldDropdownMatchTriggerWidth}
                  style={{
                    ...floatingStyles,
                    outline: '0',
                    minHeight: dropdownMinHeight,
                    maxHeight: dropdownMaxHeight,
                  }}
                >
                  {isLoading ? (
                    <View
                      aria-label="Loading..."
                      display="flex"
                      justifyContent="center"
                      padding="$5 $0"
                      role="option"
                    >
                      <Spinner />
                    </View>
                  ) : (
                    <SelectMenuOptionsContainer
                      ref={optionsContainerRef}
                      style={{
                        minHeight: scrollableOptionsMinHeight,
                        maxHeight: scrollableOptionsMaxHeight,
                      }}
                    >
                      <TooltipGroup>
                        {options.map((option, index) => {
                          if (isOptionGroup(option)) {
                            return (
                              <React.Fragment key={option.label as string}>
                                {index !== 0 ? (
                                  <Divider elevation={1} spacing={1} />
                                ) : null}
                                <OptionGroup
                                  label={option.label}
                                  shouldHideLabel={option.shouldHideLabel}
                                  suffix={option.suffix}
                                >
                                  {option.options.map((childOption) => (
                                    <Option
                                      activeIndex={activeIndex}
                                      getItemProps={getItemProps}
                                      index={getIndexFromValue({
                                        optionsMap,
                                        value: childOption.value,
                                      })}
                                      isBranded={isBranded}
                                      key={childOption.value}
                                      listContentRef={listContentRef}
                                      listRef={listRef}
                                      onChange={onChange}
                                      onRequestClose={onRequestClose}
                                      option={childOption}
                                      optionTruncationProps={
                                        optionTruncationProps
                                      }
                                      selectedIndex={selectedIndex}
                                      setIsOpen={setIsOpen}
                                      setSelectedIndex={setSelectedIndex}
                                      shouldNavigateDisabledOptions={
                                        shouldNavigateDisabledOptions
                                      }
                                      verticalOptionAlignment={
                                        verticalOptionAlignment
                                      }
                                    />
                                  ))}
                                </OptionGroup>
                              </React.Fragment>
                            );
                          }

                          return (
                            <Option
                              activeIndex={activeIndex}
                              getItemProps={getItemProps}
                              index={getIndexFromValue({
                                optionsMap,
                                value: option.value,
                              })}
                              isBranded={isBranded}
                              key={option.value}
                              listContentRef={listContentRef}
                              listRef={listRef}
                              onChange={onChange}
                              onRequestClose={onRequestClose}
                              option={option}
                              optionTruncationProps={optionTruncationProps}
                              selectedIndex={selectedIndex}
                              setIsOpen={setIsOpen}
                              setSelectedIndex={setSelectedIndex}
                              shouldNavigateDisabledOptions={
                                shouldNavigateDisabledOptions
                              }
                              verticalOptionAlignment={verticalOptionAlignment}
                            />
                          );
                        })}
                      </TooltipGroup>
                    </SelectMenuOptionsContainer>
                  )}
                  {noteTextToShow ? (
                    <NoteContainerView
                      ref={noteContainerRef}
                      shouldNotMatchTriggerWidth={
                        !shouldDropdownMatchTriggerWidth
                      }
                    >
                      <Divider elevation={1} spacing={1} />
                      <NoteView
                        hasIcon={false}
                        id={SELECT_NOTE_ID}
                        style={{minHeight: noteMinHeight}}
                      >
                        {noteTextToShow}
                      </NoteView>
                    </NoteContainerView>
                  ) : null}
                </SelectMenu>
              </FloatingFocusManager>
            </FloatingOverlay>
          </OverlayPortal>
        )}
      </FloatingNode>
    </SelectWrapperView>
  );
};

export const Select = fixedForwardRef(SelectBase);

type OptionProps = {
  activeIndex: number | null;
  getItemProps: any;
  index: number;
  isBranded?: boolean;
  listContentRef: {current: Array<string | null>};
  listRef: {current: Array<HTMLElement | null>};
  onChange?: SelectProps['onChange'];
  onRequestClose?: (event?: Event | React.SyntheticEvent) => void;
  option: SelectOption;
  selectedIndex: number;
  setIsOpen: (isOpen: boolean) => void;
  setSelectedIndex: (index: number) => void;
  shouldNavigateDisabledOptions: boolean;
  verticalOptionAlignment: 'center' | 'top';
};

const Option = ({
  activeIndex,
  getItemProps,
  index,
  isBranded,
  listContentRef,
  listRef,
  onChange,
  onRequestClose,
  option,
  optionTruncationProps = emptyObject,
  selectedIndex,
  setIsOpen,
  setSelectedIndex,
  shouldNavigateDisabledOptions,
  verticalOptionAlignment,
}: OptionProps & {
  optionTruncationProps?: SelectProps['optionTruncationProps'];
}) => {
  const [anchorRef, setAnchorRef] = React.useState<HTMLButtonElement | null>(
    null
  );

  const [isEscapePressed, setIsEscapePressed] = React.useState(false);
  const isActive = activeIndex === index;
  const isSelected = selectedIndex === index;
  const shouldTruncate = Boolean(Object.keys(optionTruncationProps).length);
  const {
    label: labelTruncation,
    description: descriptionTruncation,
    maxWidth: truncationTooltipMaxWidth,
  } = optionTruncationProps;

  const handleOptionClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const isUserInTooltip = Boolean(
        // @ts-expect-error - TS2339 - Property 'closest' does not exist on type 'EventTarget'.
        event.target.closest('[data-sc*="Tooltip"]')
      );

      /**
       * Because we use `aria-disabled` instead of `disabled`, we must stop the click event from happening on disabled options.
       *
       * In addition, if a user's mouse is over a tooltip created by an Option, we want to prevent the click from firing.
       */
      if (option.isDisabled || option.disabled || isUserInTooltip) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (option.customClickAction) {
        option.customClickAction();
      } else {
        /**
         * Need to figure out how to pass the correct type for `onChange` between `Select` and `Option`.
         * This type will be derived from the generic `TOptions`, but scoped for the specific option (if possible).
         */
        // @ts-expect-error - TS2345 - Argument of type 'string | number | null | undefined' is not assignable to parameter of type 'string | null | undefined'.
        onChange?.(option.value, {option, changeType: 'changeEnd'});
        setSelectedIndex(index);
      }
      setIsOpen(false);
      onRequestClose?.(event);
    },
    [index, onChange, onRequestClose, setIsOpen, setSelectedIndex, option]
  );

  React.useEffect(() => {
    setIsEscapePressed(false);
  }, [isActive]);

  const itemProps = React.useMemo(
    () =>
      getItemProps({
        onClick(event: React.MouseEvent<HTMLButtonElement>) {
          handleOptionClick(event);
        },
        onKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
          if (e.key === 'Escape') {
            /**
             * Because our `label` and `description` tooltips are using a controlled opened state,
             * but our focus is on the wrapping `Option` element, the `Tooltip` will not capture the `Escape` event.
             *
             * We need to manually close the tooltip when a user presses escape and a tooltip is open.
             */
            setIsEscapePressed(true);
          }
        },
      }),
    [getItemProps, handleOptionClick]
  );

  const textTruncationProps = React.useMemo(
    () =>
      ({
        placement: 'right',
        anchorRef,
        isOpen: isActive && !isEscapePressed,
        /** Noop, we're relying on isActive & isEscapePressed for our change management */
        onOpenChange: noop,
        label: (
          <VStack spacing={0}>
            {option.label ? (
              <Text colorVariant="primary">{option.label}</Text>
            ) : null}
            {option.description ? (
              <Text colorVariant="secondary">{option.description}</Text>
            ) : null}
          </VStack>
        ),
        maxWidth: truncationTooltipMaxWidth ?? 320,
        isHoverable: true,
        _useDismissProps: {
          bubbles: {
            outsidePress: true,
          },
        },
      }) as const,
    [
      anchorRef,
      isEscapePressed,
      isActive,
      option.label,
      option.description,
      truncationTooltipMaxWidth,
    ]
  );

  const explicitTooltipProps = React.useMemo(
    () =>
      option.tooltipProps
        ? {
            ...option.tooltipProps,
            ...textTruncationProps,
            label: option.tooltipProps.label,
          }
        : emptyObject,
    [option, textTruncationProps]
  );

  // NOTE: We support the use of either `isDisabled` or `disabled` on the option object
  const isOptionDisabled = option.isDisabled || option.disabled;

  return (
    <OptionView
      aria-describedby={SELECT_NOTE_ID}
      aria-selected={isSelected}
      data-automation-id={`select-option-${option.value}`}
      data-roving-tabindex-item
      isActive={isActive}
      isBranded={isBranded}
      isDisabled={isOptionDisabled}
      isSelected={isSelected}
      key={option.value}
      ref={(node) => {
        // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
        listRef.current[index] = node;
        listContentRef.current[index] = option.label as string;
        setAnchorRef((current) => {
          if (!current) {
            return node;
          }
          return current;
        });
      }}
      role="option"
      shouldNavigateDisabledOptions={shouldNavigateDisabledOptions}
      tabIndex={isActive ? 0 : -1}
      {...itemProps}
    >
      <Tooltip
        asChild
        isDisabled={!option.tooltipProps}
        isHoverable
        {...explicitTooltipProps}
      >
        <OptionHStack verticalOptionAlignment={verticalOptionAlignment}>
          {!(option.customClickAction && option.icon) ? (
            <StackItem>
              {isSelected ? (
                <CheckmarkView isBranded={isBranded}>
                  <CheckDefaultIcon colorVariant="inherit" />
                </CheckmarkView>
              ) : (
                <EmptyIconSpacer />
              )}
            </StackItem>
          ) : null}
          {option.icon ? (
            <StackItem>
              <OptionIconPrefixView
                isActive={isActive}
                isBranded={isBranded}
                isDisabled={isOptionDisabled}
              >
                {renderElement(option.icon)}
              </OptionIconPrefixView>
            </StackItem>
          ) : null}
          <VStack
            spacing={0}
            style={{minWidth: shouldTruncate ? 0 : undefined}}
          >
            <HStack align="start">
              <Text
                colorVariant="primary"
                {...labelTruncation}
                truncationTooltipProps={
                  labelTruncation ? textTruncationProps : undefined
                }
              >
                {option.label}
              </Text>
              {option.suffix ? (
                <>
                  <Spacer />
                  <View flexShrink={0}>
                    <Text colorVariant="secondary">{option.suffix}</Text>
                  </View>
                </>
              ) : null}
            </HStack>
            {option.description ? (
              <Text
                colorVariant="secondary"
                {...descriptionTruncation}
                truncationTooltipProps={
                  descriptionTruncation ? textTruncationProps : undefined
                }
              >
                {option.description}
              </Text>
            ) : null}
          </VStack>
        </OptionHStack>
      </Tooltip>
    </OptionView>
  );
};

type OptionGroupProps = {
  children: React.ReactNode;
  label: React.ReactNode;
  shouldHideLabel?: boolean;
  suffix: React.ReactNode;
};

const OptionGroup = ({
  children,
  label,
  suffix,
  shouldHideLabel,
}: OptionGroupProps) => {
  if (!label) {
    return <>{children}</>;
  }

  const ariaLabel = typeof label === 'string' ? label : undefined;

  return (
    <>
      {shouldHideLabel ? null : (
        <OptionGroupView>
          <HStack>
            <Text font="heading">{label}</Text>
            {suffix ? (
              <>
                <Spacer />
                <View flexShrink={0}>
                  <Text>{suffix}</Text>
                </View>
              </>
            ) : null}
          </HStack>
        </OptionGroupView>
      )}
      <div aria-label={ariaLabel} role="group">
        {children}
      </div>
    </>
  );
};

export default Select;
